import { useEffect, useState, type ReactNode } from "react";
import { Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export function OptionCard({
  selected,
  title,
  hint,
  onClick,
  disabled,
}: {
  selected: boolean;
  title: string;
  hint?: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        "flex flex-col gap-0.5 rounded-lg border bg-background px-3 py-2.5 text-left text-sm transition-colors",
        "hover:border-amber-400 disabled:cursor-not-allowed disabled:opacity-50",
        selected && "border-2 border-amber-400 bg-amber-50",
      )}
    >
      <span className="font-medium text-slate-900">{title}</span>
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
    </button>
  );
}

export function Pills<T extends string | number>({
  value,
  options,
  onChange,
  disabled,
  label,
}: {
  value: T | null;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={String(o.value)}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={disabled}
            onClick={() => onChange(o.value)}
            className={cn(
              "rounded-full border px-3 py-1 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-60",
              on ? "border-slate-900 bg-slate-100 font-medium text-slate-900" : "text-muted-foreground hover:border-slate-400",
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function MultiPills<T extends string>({
  values,
  options,
  onToggle,
  label,
}: {
  values: T[];
  options: { value: T; label: string }[];
  onToggle: (value: T) => void;
  label: string;
}) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const on = values.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={on}
            onClick={() => onToggle(o.value)}
            className={cn(
              "rounded-full border px-3 py-1 text-xs transition-colors",
              on ? "border-slate-900 bg-slate-100 font-medium text-slate-900" : "text-muted-foreground hover:border-slate-400",
            )}
          >
            {on && "✓ "}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function QtyStepper({
  value,
  max,
  onChange,
  disabled,
}: {
  value: number;
  max: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}) {
  return (
    <div className="inline-flex items-center gap-1.5">
      <Button type="button" variant="outline" size="icon" className="h-7 w-7" disabled={disabled} onClick={() => onChange(value - 1)} aria-label="Fewer">
        <Minus className="h-3 w-3" />
      </Button>
      <span className="w-6 text-center tabular-nums">{value}</span>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-7 w-7"
        disabled={disabled || value >= max}
        onClick={() => onChange(value + 1)}
        aria-label="More"
      >
        <Plus className="h-3 w-3" />
      </Button>
    </div>
  );
}

export function CoverBox({
  percent,
  value,
  onChange,
  disabled,
  label,
  each,
}: {
  percent: boolean;
  value: number;
  onChange: (percent: boolean, value: number) => void;
  disabled?: boolean;
  label: string;
  each?: boolean;
}) {
  const [text, setText] = useState(value ? String(value) : "");
  useEffect(() => {
    setText((t) => (Number(t || 0) === value ? t : value ? String(value) : ""));
  }, [value]);
  return (
    <div className="inline-flex items-center gap-1.5">
      <div className={cn("inline-flex h-8 items-center overflow-hidden rounded-md border bg-background", disabled && "opacity-50")}>
        <button
          type="button"
          disabled={disabled}
          title="Switch between $ and %"
          onClick={() => {
            setText("");
            onChange(!percent, 0);
          }}
          className="h-full min-w-7 border-r bg-slate-50 px-2 text-xs font-medium text-slate-600 hover:bg-slate-100"
        >
          {percent ? "%" : "$"}
        </button>
        <Input
          aria-label={label}
          inputMode="decimal"
          disabled={disabled}
          value={text}
          placeholder="0"
          onChange={(e) => {
            const cleaned = e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
            const [whole, cents] = cleaned.split(".");
            const next = cents === undefined ? whole : `${whole}.${cents.slice(0, 2)}`;
            const n = Number(next || 0);
            const capped = percent && n > 100 ? "100" : next;
            setText(capped);
            onChange(percent, Number.isFinite(n) ? Math.min(percent ? 100 : n, n) : 0);
          }}
          className="h-full w-20 rounded-none border-0 text-right shadow-none focus-visible:ring-0"
        />
      </div>
      {each && !percent && <span className="text-xs text-muted-foreground">each</span>}
    </div>
  );
}

export function SumRow({ label, value, strong, hint }: { label: string; value: string; strong?: boolean; hint?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-dashed py-1.5 text-sm last:border-b-0">
      <span className="text-slate-700">
        {label}
        {hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
      </span>
      <span className={cn("tabular-nums", strong && "font-semibold text-slate-900")}>{value}</span>
    </div>
  );
}
